import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';

import { formatDate } from 'services/valueFormatter';
import MethodLabel from 'components/common/MethodLabel';
import ClassifierCard from 'components/Requester/ClassifierCard';
import RequestHeaders from 'components/Requester/RequestHeaders';
import cx from 'classnames';
import Card from 'components/common/Card';
import FlagFilter from 'components/common/FlagFilter';
import api from 'services/api';

import 'components/common/Paginator/styles.scss';
import './style.scss';

const RequestsTable = ({ requests, hasOlder: initialHasOlder = false, tableTitle='Requests' }) => {

  const { requesterId: currentRequester } = useParams();
  const currentLogger = useSelector(state => state.global.currentLogger);
  const perPage = 10;

  const [currentRequests, setCurrentRequests] = useState(requests);
  const [hasNewer, setHasNewer] = useState(false);
  const [hasOlder, setHasOlder] = useState(initialHasOlder);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState(new Set());
  const [selectedFlags, setSelectedFlags] = useState(0);
  const latestFetch = useRef(0);

  const toggleRequest = (requestId) => {
    setExpandedRequests(previous => {
      const next = new Set(previous);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  const formatRequestValue = (value, field) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (field === 'createdAt') return formatDate(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getRequesterRequests = useCallback((direction, cursorRequest, flags) => {
    const fetchId = ++latestFetch.current;
    setIsLoading(true);
    setIsError(false);
    api.getRequesterRequests(currentLogger, currentRequester, perPage, direction, cursorRequest, flags, res => {
      if (fetchId !== latestFetch.current) return;
      if (res.data.status === 200) {
        setCurrentRequests(res.data.requests);
        setHasNewer(res.data.hasNewer);
        setHasOlder(res.data.hasOlder);
        setExpandedRequests(new Set());
      } else {
        setIsError(true);
      }
      setIsLoading(false);
    });
  }, [currentLogger, currentRequester]);

  const navButton = (direction, cursorRequest, hasPage, icon, label) => {
    const enabled = hasPage && !isLoading;
    return (
      <li>
        <a
          onClick={enabled ? () => getRequesterRequests(direction, cursorRequest, selectedFlags) : undefined}
          className={cx('pagination-controller pagination-previous', !enabled && 'page-limit')}
          aria-label={label}
          title={label}
        >
          <i className={`fas ${icon} fa-lg`}></i>
        </a>
      </li>
    );
  };

  const handleFlagsChange = (flags) => {
    setSelectedFlags(flags);
    getRequesterRequests('newest', null, flags);
  };

  const getRequestRows = () => (
    currentRequests.map((request, index) => {
      const {
        id, method, uri, timeTaken, numberQueries, createdAt,
        referer, sessionId, username, userAgent, country,
        ztr_bot, ztr_bot_cat,
        statusCode, flags, action, decidedBy
      } = request;
      const requestId = id || `${createdAt}-${uri}-${index}`;
      const isExpanded = expandedRequests.has(requestId);
      const details = [
        ['Referer', referer, 'referer'],
        ['Status code', statusCode, 'statusCode'],
        ['Session ID', sessionId, 'sessionId'],
        ['Username', username, 'username'],
        ['User agent', userAgent, 'userAgent'],
        ['Country', country, 'country'],
        ['Bot', ztr_bot, 'ztr_bot'],
        ['Bot category', ztr_bot_cat, 'ztr_bot_cat'],
      ];

      return (
        <React.Fragment key={requestId}>
          <tr
            aria-expanded={isExpanded}
            onClick={() => toggleRequest(requestId)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleRequest(requestId);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <td>
              <MethodLabel method={method} />
            </td>
            <td>
              <a className="request-uri has-text-link is-size-7 has-tooltip-top" href={uri} data-tooltip={uri} target="_blank" rel="noreferrer" onClick={event => event.stopPropagation()}>
                {uri}
              </a>
            </td>
            <td className="is-size-7">
              {timeTaken}
            </td>
            <td className="is-size-7">
              {numberQueries}
            </td>
            <td className="is-size-7">
              {formatDate(createdAt)}
            </td>
          </tr>
          {isExpanded && (
            <tr>
              <td colSpan={5}>
                <div className="columns is-multiline is-size-7">
                  {details.map(([label, value, field]) => (
                    <div className="column is-half" key={label}>
                      <strong>{label}:</strong> {formatRequestValue(value, field)}
                    </div>
                  ))}
                  <RequestHeaders headers={request.headers} />
                  <div className="column is-half">
                    <ClassifierCard request={request}/>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    })
  );

  return (
    <Card>
      <div className="requests-table-header">
        <h3 className="has-text-weight-bold has-text-light">
          {tableTitle}
        </h3>
        <FlagFilter value={selectedFlags} onChange={handleFlagsChange} size="small" />
        {isLoading && (
          <span className="requests-table-status has-text-grey is-size-7">
            <i className="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
            Loading requests…
          </span>
        )}
        {isError && !isLoading && (
          <span className="requests-table-status has-text-danger is-size-7">
            Failed to load requests
          </span>
        )}
      </div>
      <table className={cx('table is-fullwidth is-narrow is-striped requests-table', isLoading && 'is-loading-requests')} aria-busy={isLoading}>
        <thead>
          <tr>
            <th>Method</th>
            <th>URI</th>
            <th>Time Taken</th>
            <th>Queries</th>
            <th>Timestamp</th>
          </tr>
        </thead>
        <tbody>
          {currentRequests.length ? getRequestRows() : (
            <tr>
              <td colSpan={5} className="has-text-centered has-text-grey is-size-7">
                {selectedFlags ? 'No requests with the selected flags' : 'No requests'}
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}>
              <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                <ul className="pagination-list">
                  {navButton('newest', null, hasNewer, 'fa-angle-double-left', 'Newest requests')}
                  {navButton('newer', currentRequests[0], hasNewer, 'fa-chevron-left', 'Newer requests')}
                  {navButton('older', currentRequests[currentRequests.length - 1], hasOlder, 'fa-chevron-right', 'Older requests')}
                  {navButton('oldest', null, hasOlder, 'fa-angle-double-right', 'Oldest requests')}
                </ul>
              </nav>
            </td>
          </tr>
        </tfoot>
      </table>
    </Card>
  );
};

RequestsTable.propTypes = {
  requests: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      method: PropTypes.string,
      uri: PropTypes.string,
      timeTaken: PropTypes.any,
      numberQueries: PropTypes.any,
      createdAt: PropTypes.string
    })
  ),
  hasOlder: PropTypes.bool,
  tableTitle: PropTypes.string
};

export default RequestsTable;
